import React, { useCallback, useEffect, useState } from 'react';
import './Appointments.scss';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { useSelector } from 'react-redux';
import { Api } from '../../api';
import { DashboardLayout } from '../../layouts/dashboard-layout/DashboardLayout';
import { ApplicationState } from '../../app/store';
import { WrittenAppointment } from '../../models/WrittenAppointment';
import { ETableColumnAlign, ResponsiveTable, ResponsiveTableColumn } from '../../features/responsive-table/ResponsiveTable';
import InvisibleInput from './InvisibleInput';
import PersonSelector from './PersonSelector';
import { DossierMember } from '../../models/DossierMember';
import { RoundButton } from '../../features/round-button/RoundButton';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import { ConfirmModal } from '../../features/confirm-modal/ConfirmModal';
import PersonsDisplay from './PersonsDisplay';
import { ISessionAppState } from '../../app/session/reducer';
import { UserAccountRoleEnum, UserRelationRoleEnum } from '../../api/types';

interface IAppointmentsTableData {
  members: JSX.Element;
  date: JSX.Element;
  time: JSX.Element;
  location: JSX.Element;
  description: JSX.Element;
  buttons: JSX.Element;
}

interface INewEntryData {
  dossierRelationIds: number[];
  date?: string;
  time?: string;
  location?: string;
  description?: string;
}

function Appointments(): JSX.Element {
  const { dossier, relation: dossierRelation, user } = useSelector<ApplicationState, ISessionAppState>(state => state.session);
  const [loadingAppointments, setLoadingAppointments] = useState<boolean>(true);
  const [loadingError, setLoadingError] = useState<string|undefined>(undefined);
  const [appointments, setAppointments] = useState<WrittenAppointment[]>([]);
  const [data, setData] = useState<IAppointmentsTableData[]>([]);
  const [firstRow, setFirstRow] = useState<IAppointmentsTableData|undefined>();
  const [savingNewEntry, setSavingNewEntry] = useState<boolean>(false);
  const [entryToDelete, setEntryToDelete] = useState<number|undefined>(undefined);
  const [deletingEntry, setDeletingEntry] = useState<boolean>(false);
  const [newEntryData, setNewEntryData] = useState<INewEntryData>({ dossierRelationIds: [] });
  const [loadingSelectablePersons, setLoadingSelectablePersons] = useState<boolean>(true);
  const [selectablePersons, setSelectablePersons] = useState<DossierMember[]>([]);
  const isAllowedToEdit = !dossier?.archived && (
    dossierRelation?.role === UserRelationRoleEnum.caregiver ||
    (user && [UserAccountRoleEnum.adminCaregiver, UserAccountRoleEnum.administration].includes(user.accountRole))
  );

  const columns: ResponsiveTableColumn<IAppointmentsTableData>[] = [
    { title: 'Deelnemers', objectKey: 'members', width: '15rem', verticalAlign: ETableColumnAlign.CENTER, disableSort: true },
    { title: 'Datum', objectKey: 'date', width: '6.5rem', verticalAlign: ETableColumnAlign.CENTER, disableSort: true },
    { title: 'Tijd', objectKey: 'time', width: '6rem', verticalAlign: ETableColumnAlign.CENTER, disableSort: true },
    { title: 'Waar', objectKey: 'location', width: '8rem', verticalAlign: ETableColumnAlign.CENTER, disableSort: true },
    { title: 'Beschrijving', objectKey: 'description', verticalAlign: ETableColumnAlign.CENTER, disableSort: true },
  ];

  if (isAllowedToEdit) {
    columns.push({ title: '', objectKey: 'buttons', verticalAlign: ETableColumnAlign.CENTER, horizontalAlign: ETableColumnAlign.END, disableSort: true, hideBorder: true });
  }

  useEffect(() => {
    // Load appointments
    if (!dossier) {
      console.error('Dossier not set');
      setLoadingError('Laden mislukt, omdat er geen dossier geselecteerd is.');
      setLoadingAppointments(false);
      setSelectablePersons([]);
      return;
    }

    setLoadingAppointments(true);
    setLoadingSelectablePersons(true);
    setLoadingError(undefined);

    Api.listAppointments(dossier.id)
      .then(response => setAppointments(response))
      .catch(err => {
        console.error('Failed to load appointments', err);
        setLoadingError('Laden afspraken mislukt door een onverwachte fout.');
      })
      .finally(() => setLoadingAppointments(false));

    // Load persons
    Api.listDossierRelations(dossier.id)
      .then(response => setSelectablePersons(response))
      .catch(err => {
        setSelectablePersons([]);
        console.error('Failed to load dossier relations', err);
      })
      .finally(() => setLoadingSelectablePersons(false));
  }, [dossier]);

  const handleInputChange = useCallback((input: keyof INewEntryData, newValue: INewEntryData[typeof input]) => {
    setNewEntryData({ ...newEntryData, [input]: newValue });
  }, [newEntryData]);

  const handlePersonSelect = useCallback((person: DossierMember): void => {
    const currentSelection: number[] = [...newEntryData.dossierRelationIds];
    const index = currentSelection.indexOf(person.memberId);
    if (currentSelection.includes(person.memberId)) {
      currentSelection.splice(index, 1);
    } else {
      currentSelection.push(person.memberId);
    }
    handleInputChange('dossierRelationIds', currentSelection);
  }, [newEntryData, handleInputChange]);

  const handleSaveNewAppointment = useCallback(() => {
    const { date, time, location, description, dossierRelationIds } = newEntryData;
    if (dossierRelationIds.length === 0) {
      console.error('Tried to save new appointment without any dossierRelations');
      return; // We are not toasting an error here since this state should have been prevented by disabling the button.
    }

    if (!dossier) {
      console.error('Tried to save new appointment without an dossier selected');
      return;
    }

    setSavingNewEntry(true);
    Api.createAppointment(dossier.id, {
      forDossierRelationIds: dossierRelationIds,
      date,
      time,
      location,
      description,
    })
      .then(newAppointment => {
        setAppointments([newAppointment, ...appointments]);
        setNewEntryData({ dossierRelationIds: [] });
      })
      .catch(err => {
        console.error('Failed to save new appointment:', err);
        NotificationHelper.toastError('Aanmaken van afspraak mislukt door een onverwachte fout.');
      })
      .finally(() => setSavingNewEntry(false));
  }, [newEntryData, appointments, dossier]);

  const handleDeleteAppointment = useCallback((appointmentId: number): void => {
    setEntryToDelete(appointmentId);
    // Must still be confirmed via confirmDeleteAppointment
  }, []);

  const confirmDeleteAppointment = useCallback((): void => {
    if (!entryToDelete) {
      return;
    }

    setDeletingEntry(true);
    Api.deleteAppointment(entryToDelete)
      .then(() => {
        const withoutDeletedAppointment = appointments.filter(appointment => appointment.id !== entryToDelete);
        setAppointments(withoutDeletedAppointment);
      })
      .catch(err => {
        console.error('Failed to delete appointment:', err);
        NotificationHelper.toastError('Verwijderen van afspraak mislukt door een onverwachte fout.');
      })
      .finally(() => {
        setEntryToDelete(undefined);
        setDeletingEntry(false);
      });
  }, [entryToDelete, appointments]);

  const cancelDeleteAppointment = (): void => {
    setEntryToDelete(undefined);
    setDeletingEntry(false);
  };

  useEffect(() => {
    if (!isAllowedToEdit) {
      setFirstRow(undefined);
      return;
    }

    const { date, time, location, description, dossierRelationIds } = newEntryData;
    const className = 'appointments__new-appointment-input';
    const selectedPersons = selectablePersons.filter(person => dossierRelationIds.includes(person.memberId));
    const row: IAppointmentsTableData = {
      members: <PersonSelector persons={selectablePersons} selected={selectedPersons} onClickItem={handlePersonSelect} />,
      date: <InvisibleInput className={className} value={date || ''} placeholder="dd/mm/yy" onChange={value => handleInputChange('date', value)} />,
      time: <InvisibleInput className={className} value={time || ''} placeholder="00:00" onChange={value => handleInputChange('time', value)} />,
      location: <InvisibleInput className={className} value={location || ''} placeholder="Locatie" onChange={value => handleInputChange('location', value)} />,
      description: <InvisibleInput className={className} value={description || ''} placeholder="Notitie" onChange={value => handleInputChange('description', value)} />,
      buttons: <div className="appointments-table__button"><RoundButton label="Afspraak opslaan" type="confirm" disabled={selectedPersons.length === 0} loading={savingNewEntry} onClick={handleSaveNewAppointment} /></div>,
    };
    setFirstRow(row);
  }, [isAllowedToEdit, newEntryData, selectablePersons, savingNewEntry, handleInputChange, handlePersonSelect, handleSaveNewAppointment]);

  useEffect(() => {
    const data: IAppointmentsTableData[] = appointments.map(appointment => {
      const persons: DossierMember[] = [];
      let unresolvedPersons = 0;
      appointment.forDossierRelationIds.forEach(relationId => {
        const person = selectablePersons.find(dossierMember => dossierMember.memberId === relationId);
        if (person) {
          persons.push(person);
        } else {
          unresolvedPersons++;
        }
      });

      return {
        members: <>
          <PersonsDisplay persons={persons} plural="deelnemers"/>
          { !!unresolvedPersons && <Alert className="appointments__danger" variant="danger">Niet alle deelnemers kunnen weergegeven worden</Alert> }
        </>,
        date: <span className="appointments__column--wrap">{appointment.date}</span>,
        time: <span className="appointments__column--wrap">{appointment.time}</span>,
        location: <span className="appointments__column--wrap">{appointment.location}</span>,
        description: <span className="appointments__column--wrap">{appointment.description}</span>,
        buttons: <div className="appointments-table__button"> <RoundButton label="Afspraak verwijderen" type="delete" disabled={!!deletingEntry} onClick={() => handleDeleteAppointment(appointment.id)} /> </div>,
      };
    });
    setData(data);
  }, [appointments, deletingEntry, selectablePersons, handleDeleteAppointment]);

  const shouldDisplay = !loadingError && !loadingAppointments && !loadingSelectablePersons;
  return <>
    <DashboardLayout className="appointments" grayBackgroundMobile={true}>
      <h1 className="page-title">Afspraken</h1>
      { loadingError && <Alert className="appointments__info" variant="danger">{loadingError}</Alert> }
      { loadingAppointments && <Spinner animation="border" role="status" /> }
      { shouldDisplay && <ResponsiveTable<IAppointmentsTableData> className="appointments__table" columns={columns} data={data} firstRow={firstRow} largeCellPadding={true} /> }
      { (shouldDisplay && data.length === 0) && <Alert className="appointments__info" variant="info">Er zijn nog geen afspraken</Alert> }
    </DashboardLayout>

    {
      entryToDelete &&
        <ConfirmModal
          title="Afspraak verwijderen"
          body="Weet je zeker dat je de afspraak wilt verwijderen?"
          confirmLabel="Verwijderen"
          confirmButtonStyle="danger"
          onCancel={cancelDeleteAppointment}
          onConfirm={confirmDeleteAppointment}
          confirming={deletingEntry}
        />
    }
  </>;
}

export default Appointments;
